import React, { useRef, useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import Swal from 'sweetalert2';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import PageBanner from '../layout/PageBanner';
import BlogSidebar from '../components/BlogSidebar';
import TouchStart from '../components/TouchStart';
import { BlogPost } from '../types/blog';
import blogService from '../services/blogService';
import 'react-quill/dist/quill.snow.css';

// Get API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8083';

const BlogDetail = () => {
    const { t } = useTranslation();
    const form = useRef<HTMLFormElement>(null);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const slug = searchParams.get('slug');

    const [post, setPost] = useState<BlogPost | null>(null);
    const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
    const [notFound, setNotFound] = useState(false);
    const [loading, setLoading] = useState(true);

    // Helper function to construct full image URL
    const getImageUrl = (imagePath: string | undefined): string => {
        if (!imagePath) return '';
        if (imagePath.startsWith('/storage/')) {
            return `${API_BASE_URL}${imagePath}`;
        }
        if (imagePath.startsWith('http')) {
            return imagePath;
        }
        return imagePath;
    };

    useEffect(() => {
        if (!slug) {
            setNotFound(true);
            setLoading(false);
            return;
        }

        loadPost();
    }, [slug]);

    const loadPost = async () => {
        try {
            setLoading(true);
            // Load post from API by slug
            const response = await blogService.getBySlug(slug!);

            if (response.success && response.data) {
                const postData = response.data;

                // Convert API response to BlogPost format
                const convertedPost: BlogPost = {
                    id: postData.public_id || `blog-${Date.now()}`,  // ✅ USE PUBLIC_ID
                    title: postData.title,
                    slug: postData.slug,
                    content: postData.content,
                    excerpt: postData.excerpt,
                    featuredImage: postData.featured_image || '',
                    images: postData.featured_image ? [postData.featured_image] : [],
                    author: postData.author,
                    publishDate: postData.publish_date ? postData.publish_date.split('T')[0] : new Date().toISOString().split('T')[0],
                    lastModified: postData.updated_at,
                    status: postData.status,
                    categories: postData.category ? [postData.category.name] : [],
                    tags: postData.tags?.map((t: any) => t.name) || [],
                    seo: {
                        metaTitle: postData.meta_title || postData.title,
                        metaDescription: postData.meta_description || postData.excerpt,
                        metaKeywords: postData.meta_keywords?.split(',').map((k: string) => k.trim()) || [],
                        ogTitle: postData.og_title || postData.title,
                        ogDescription: postData.og_description || postData.excerpt,
                        ogImage: postData.og_image || postData.featured_image,
                        canonicalUrl: postData.canonical_url || `/blog/${postData.slug}`,
                    },
                    views: postData.views || 0,
                    commentsEnabled: postData.comments_enabled ?? true,
                };

                setPost(convertedPost);

                // Increment view count by public_id
                if (postData.public_id) {
                    blogService.incrementViewsByPublicId(postData.public_id);
                }

                // Load related posts by public_id
                if (postData.public_id) {
                    try {
                        const relatedResponse = await blogService.getRelatedByPublicId(postData.public_id, 2);
                        if (relatedResponse.success && relatedResponse.data) {
                            const convertedRelated = relatedResponse.data.map((p: any) => ({
                                id: p.public_id || `blog-${Date.now()}`,
                                title: p.title,
                                slug: p.slug,
                                content: p.content,
                                excerpt: p.excerpt,
                                featuredImage: p.featured_image || '',
                                images: p.featured_image ? [p.featured_image] : [],
                                author: p.author,
                                publishDate: p.publish_date ? p.publish_date.split('T')[0] : new Date().toISOString().split('T')[0],
                                lastModified: p.updated_at,
                                status: p.status,
                                categories: p.category ? [p.category.name] : [],
                                tags: p.tags?.map((t: any) => t.name) || [],
                                seo: {
                                    metaTitle: p.meta_title || p.title,
                                    metaDescription: p.meta_description || p.excerpt,
                                    metaKeywords: p.meta_keywords?.split(',').map((k: string) => k.trim()) || [],
                                    ogTitle: p.og_title || p.title,
                                    ogDescription: p.og_description || p.excerpt,
                                    ogImage: p.og_image || p.featured_image,
                                    canonicalUrl: p.canonical_url || `/blog/${p.slug}`,
                                },
                                views: p.views || 0,
                                commentsEnabled: p.comments_enabled ?? true,
                            }));
                            setRelatedPosts(convertedRelated);
                        }
                    } catch (error) {
                        console.error('Error loading related posts:', error);
                        setRelatedPosts([]);
                    }
                }
            } else {
                setNotFound(true);
            }
        } catch (error) {
            console.error('Error loading blog post:', error);
            setNotFound(true);
        } finally {
            setLoading(false);
        }
    };

    const sendEmail = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const target = e.target as HTMLFormElement;
        emailjs.sendForm('gmail', 'YOUR_TEMPLATE_ID', target, 'd9b2e0f5fc72cb94792110e8ff2028f3-us16')
            .then((result) => {
                console.log(result.text);
            }, (error) => {
                console.log(error.text);
            });
        target.reset();
        Swal.fire({ title: t('blogDetailPage.formSuccess'), text: t('blogDetailPage.formSuccessText'), icon: "success" });
    };

    const handlePostClick = (postSlug: string) => {
        navigate(`/blog-detail?slug=${postSlug}`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (notFound) {
        return (
            <>
                <PageBanner parentPage="Blog" activePage="Post Not Found" motherMenu="Blog" />
                <section className="main-our-blog page-our-blog">
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-lg-8">
                                <div className="text-center py-5">
                                    <div style={{ fontSize: '4rem' }} className="mb-3">📝</div>
                                    <h3 className="mb-3">Blog Post Not Found</h3>
                                    <p className="text-muted mb-4">The blog post you're looking for doesn't exist or has been removed.</p>
                                    <Link to="/blog-list" className="sec-btn">
                                        <span>Back to Blog List</span>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </>
        );
    }

    if (!post) {
        return (
            <>
                <PageBanner parentPage="Blog" activePage="Loading..." motherMenu="Blog" />
                <section className="main-our-blog page-our-blog">
                    <div className="container">
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    </div>
                </section>
            </>
        );
    }

    return (
        <>
            <Helmet>
                <title>{post.seo.metaTitle}</title>
                <meta name="description" content={post.seo.metaDescription} />
                <meta name="keywords" content={post.seo.metaKeywords.join(', ')} />
                <meta property="og:title" content={post.seo.ogTitle || post.title} />
                <meta property="og:description" content={post.seo.ogDescription || post.excerpt} />
                <meta property="og:image" content={post.seo.ogImage || post.featuredImage} />
                <meta property="og:type" content="article" />
                <link rel="canonical" href={post.seo.canonicalUrl || `https://pure-key-homes.es/blog/${post.slug}`} />
            </Helmet>

            <PageBanner
                parentPage={t('blogDetailPage.parentPage')}
                activePage={post.title}
                motherMenu={t('blogDetailPage.motherMenu')}
            />
            <section className="main-our-blog page-our-blog">
                <span className="blog-content-shape"></span>
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8">
                            <div className="blog-page-content">
                                <div className="page-blog-post blog-post-detail">
                                    <div className="blog-img-box">
                                        {post.featuredImage && (
                                            <div
                                                className="blog-img back-img"
                                                style={{
                                                    backgroundImage: `url(${getImageUrl(post.featuredImage)})`,
                                                    height: '400px',
                                                    backgroundSize: 'cover',
                                                    backgroundPosition: 'center'
                                                }}
                                            ></div>
                                        )}
                                        <div className="blog-date">
                                            <span title={new Date(post.publishDate).toLocaleDateString()}>
                                                <i className="fa fa-calendar" aria-hidden="true"></i>
                                                {new Date(post.publishDate).toLocaleDateString('en-GB', {
                                                    day: 'numeric',
                                                    month: 'long',
                                                    year: 'numeric'
                                                })}
                                            </span>
                                            <span className="by-admin" title={`By ${post.author}`}>
                                                <i className="fa fa-user" aria-hidden="true"></i>
                                                By {post.author}
                                            </span>
                                            {post.views !== undefined && post.views > 0 && (
                                                <span className="by-admin" title={`${post.views} views`}>
                                                    <i className="fa fa-eye" aria-hidden="true"></i>
                                                    {post.views} views
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <h3 className="h3-title">{post.title}</h3>

                                    {post.categories.length > 0 && (
                                        <div className="mb-3">
                                            {post.categories.map((category, idx) => (
                                                <span
                                                    key={idx}
                                                    className="badge bg-primary me-1"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => navigate(`/blog-list?category=${encodeURIComponent(category)}`)}
                                                >
                                                    {category}
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    <div
                                        className="page-blog-text ql-editor"
                                        dangerouslySetInnerHTML={{ __html: post.content }}
                                        style={{
                                            fontSize: '16px',
                                            lineHeight: '1.8',
                                            color: '#5a6c7d'
                                        }}
                                    />

                                    <div className="blog-post-footer">
                                        <div className="tag">
                                            <p><i className="fa fa-tag" />{t('blogDetailPage.tag')}</p>
                                            <ul>
                                                {post.tags.map((tag, idx) => (
                                                    <li key={idx}>
                                                        <Link
                                                            to="#"
                                                            title={tag}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                navigate(`/blog-list?tag=${encodeURIComponent(tag)}`);
                                                            }}
                                                        >
                                                            {tag}
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className="blog-post-social">
                                            <Link
                                                to={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                                                target="_blank"
                                                title="Share on Facebook"
                                            >
                                                <i className="fa fa-facebook" />
                                            </Link>
                                            <Link
                                                to={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`}
                                                target="_blank"
                                                title="Share on Twitter"
                                            >
                                                <i className="fa fa-twitter" />
                                            </Link>
                                            <Link
                                                to={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(post.title)}`}
                                                target="_blank"
                                                title="Share on LinkedIn"
                                            >
                                                <i className="fa fa-linkedin" />
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {relatedPosts.length > 0 && (
                                <div className="related-blog">
                                    <span className="sub-title">{t('blogDetailPage.relatedBlogSubtitle')}</span>
                                    <h2 className="h2-title">{t('blogDetailPage.relatedBlog')}</h2>
                                    <div className="row">
                                        {relatedPosts.map((relatedPost) => (
                                            <div className="col-md-6" key={relatedPost.id}>
                                                <div className="blog-box" onClick={() => handlePostClick(relatedPost.slug)} style={{ cursor: 'pointer' }}>
                                                    <div className="blog-img-box">
                                                        {relatedPost.featuredImage ? (
                                                            <div
                                                                className="blog-img back-img"
                                                                style={{
                                                                    backgroundImage: `url(${getImageUrl(relatedPost.featuredImage)})`,
                                                                    height: '200px',
                                                                    backgroundSize: 'cover',
                                                                    backgroundPosition: 'center'
                                                                }}
                                                            ></div>
                                                        ) : (
                                                            <div
                                                                className="blog-img back-img"
                                                                style={{
                                                                    height: '200px',
                                                                    backgroundColor: '#f0f0f0',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                            >
                                                                <i className="fa fa-image" style={{ fontSize: '48px', color: '#ccc' }}></i>
                                                            </div>
                                                        )}
                                                        <div className="blog-date">
                                                            <span>
                                                                <i className="fa fa-calendar" />
                                                                {new Date(relatedPost.publishDate).toLocaleDateString('en-GB')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="blog-text">
                                                        <h3 className="h3-title">
                                                            <Link to="#" onClick={(e) => { e.preventDefault(); handlePostClick(relatedPost.slug); }}>
                                                                {relatedPost.title}
                                                            </Link>
                                                        </h3>
                                                        <p>{relatedPost.excerpt.substring(0, 100)}...</p>
                                                        <Link to="#" onClick={(e) => { e.preventDefault(); handlePostClick(relatedPost.slug); }}>
                                                            READ MORE <i className="fa fa-angle-right" />
                                                        </Link>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                        </div>
                        <BlogSidebar
                            onCategoryFilter={(category) => {
                                navigate(`/blog-list?category=${encodeURIComponent(category)}`);
                            }}
                            onTagFilter={(tag) => {
                                navigate(`/blog-list?tag=${encodeURIComponent(tag)}`);
                            }}
                        />
                    </div>
                </div>
            </section>
            <div style={{ width: '80%', margin: '0 auto 60px auto' }}>
                <TouchStart />
            </div>
        </>
    );
};

export default BlogDetail;
